import { useEffect, useState } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

export type AppRole = "admin" | "reviewer" | "respondent" | "po" | "approver" | "master_reviewer";

export function useAuthRoles() {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [roles, setRoles] = useState<AppRole[] | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, sess) => {
      setSession(sess);
      setUser(sess?.user ?? null);
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (!session?.user) {
      setRoles(null);
      return;
    }
    // Defer RPC call outside the auth callback to avoid deadlocks
    const fetchRoles = async () => {
      const { data, error } = await (supabase as any).rpc("current_user_roles");
      if (!error && Array.isArray(data)) {
        setRoles(data as AppRole[]);
      } else {
        setRoles([]);
      }
    };
    fetchRoles();
  }, [session?.user?.id]);

  return { session, user, roles, loading } as const;
}
